import { useMemo, useState } from 'react'
import { Check, X } from 'lucide-react'
import { DEFAULT_INSURANCE, type BandSide, type Insurance, type InsuranceSetting, type WorkerDetail, type WorkerSpec } from '@shared/worker'
import { workersApi } from '../../lib/api'
import { cn } from '../../lib/utils'
import { WorkerCard } from './WorkerCard'

const btn = 'inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[12px] font-medium transition-colors disabled:opacity-50'
const input = 'w-20 rounded-md border border-[var(--line-soft)] bg-transparent px-2 py-1 text-right text-[12.5px] text-fg tabular-nums'

const num = (s: string): number | null => {
  const n = Number(s.replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null
}
const SIDES: { v: BandSide; label: string }[] = [
  { v: 'wait', label: 'wait' },
  { v: 'notify', label: 'tell me' },
  { v: 'skipDay', label: 'skip the day' },
  { v: 'pause', label: 'pause' },
  { v: 'cancel', label: 'cancel' },
]
const select = 'rounded-md border border-[var(--line-soft)] bg-transparent px-1.5 py-1 text-[12px] text-fg'

type Stall = NonNullable<InsuranceSetting['stall']>

/** The spec form of a resolved insurance (the spec leaves unset fields out instead of null). */
function toSpec(ins: InsuranceSetting): Insurance {
  const st = ins.stall
  return {
    on: ins.on,
    level: ins.level,
    basis: ins.basis,
    protectOthers: ins.protectOthers,
    ...(st
      ? { stall: { on: st.on, sigmas: st.sigmas, confidence: st.confidence, ...(st.way != null ? { way: st.way } : {}), ...(st.minutes != null ? { minutes: st.minutes } : {}) } }
      : {}),
  }
}

/**
 * The panel's quick edit of a RUNNING worker: prices, insurance and rules without the chat. It
 * builds the new spec and asks the server for a change card — nothing changes before its Approve.
 */
export function QuickEdit({ w, onClose }: { w: WorkerDetail; onClose: () => void }) {
  const spec = w.spec
  const bought = (w.runtime?.lots ?? []).some((x) => x.entryFilled > 0)
  const ins0: InsuranceSetting = { ...DEFAULT_INSURANCE, ...(w.plan.insurance ?? { on: false, protectOthers: false }) }
  const [stops, setStops] = useState(spec.stopLosses.map((s) => String(s.price)))
  const [targets, setTargets] = useState(spec.takeProfits.map((t) => String(t.price)))
  const [dropLast, setDropLast] = useState(false)
  const runner = w.plan.tpShares.reduce((a, b) => a + b, 0) < w.plan.quantity
  const [runnerTarget, setRunnerTarget] = useState('')
  const [entry, setEntry] = useState(String(spec.entry.limitPrice ?? spec.entry.stopPrice ?? ''))
  const zone = spec.entry.band
  const [zMin, setZMin] = useState(zone ? String(zone.min) : '')
  const [zMax, setZMax] = useState(zone ? String(zone.max) : '')
  const [zInvalid, setZInvalid] = useState(zone?.invalid != null ? String(zone.invalid) : '')
  const [zChase, setZChase] = useState(zone?.chase != null ? String(zone.chase) : '')
  const [zSessions, setZSessions] = useState(zone?.window?.kind === 'sessions' ? String(zone.window.count) : '')
  const [zBelow, setZBelow] = useState<BandSide>(zone?.below ?? 'notify')
  const [zAbove, setZAbove] = useState<BandSide>(zone?.above ?? 'notify')
  const [ins, setIns] = useState<InsuranceSetting>(ins0)
  const stall: Stall = { ...DEFAULT_INSURANCE.stall!, ...(ins.stall ?? {}) }
  const setStall = (patch: Partial<Stall>) => setIns({ ...ins, stall: { ...stall, ...patch } })
  const [dropRules, setDropRules] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [card, setCard] = useState<string | null>(null)
  const entryEditable = !bought && spec.entry.orderType !== 'MKT' && !zone

  const next = useMemo((): WorkerSpec | string => {
    const sl = spec.stopLosses.map((s, i) => ({ ...s, price: num(stops[i]) ?? NaN }))
    if (sl.some((s) => Number.isNaN(s.price))) return 'Every stop needs a price.'
    let tp = spec.takeProfits.map((t, i) => ({ ...t, price: num(targets[i]) ?? NaN }))
    if (tp.some((t) => Number.isNaN(t.price))) return 'Every target needs a price.'
    if (dropLast && tp.length) tp = tp.slice(0, -1)
    if (runner && runnerTarget.trim()) {
      const p = num(runnerTarget)
      if (p == null) return 'The runner’s target needs a price.'
      tp = [...tp, { price: p, qty: { kind: 'rest' } }]
    }
    const e = { ...spec.entry }
    if (zone && !bought) {
      const min = num(zMin)
      const max = num(zMax)
      if (min == null || max == null) return 'The buy zone needs both prices.'
      const count = zSessions.trim() ? Math.round(Number(zSessions)) : null
      if (count != null && !(count >= 1 && count <= 60)) return 'Sessions: 1–60.'
      const invalid = zInvalid.trim() ? num(zInvalid) : null
      const chase = zChase.trim() ? num(zChase) : null
      const { invalid: _i, chase: _c, ...rest } = zone
      e.band = {
        ...rest,
        min,
        max,
        below: zBelow,
        above: zAbove,
        ...(count != null ? { window: { kind: 'sessions' as const, count } } : {}),
        ...(invalid != null ? { invalid } : {}),
        ...(chase != null ? { chase } : {}),
      }
      delete e.limitPrice
    }
    if (entryEditable) {
      const p = num(entry)
      if (p == null) return 'The entry needs a price.'
      if (e.orderType === 'STP') e.stopPrice = p
      else e.limitPrice = p
    }
    return { ...spec, entry: e, takeProfits: tp, stopLosses: sl, insurance: toSpec(ins), rules: spec.rules.filter((r) => !dropRules.has(r.id)) }
  }, [spec, stops, targets, dropLast, runner, runnerTarget, entry, entryEditable, ins, dropRules, zone, bought, zMin, zMax, zInvalid, zChase, zSessions, zBelow, zAbove])

  const submit = async () => {
    if (typeof next === 'string') return setErr(next)
    setBusy(true)
    setErr(null)
    try {
      const c = await workersApi.revise(w.id, next)
      setCard(c.id)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  if (card) return <WorkerCard id={card} variant="page" />

  const seg = (on: boolean) => cn('rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors', on ? 'bg-[var(--color-surface-3)] text-fg' : 'text-fg-subtle hover:text-fg')
  return (
    <div className="card mt-2 space-y-3 px-3.5 py-3 text-[12.5px]">
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] font-semibold uppercase tracking-wide text-fg-faint">Quick edit · {w.name}</span>
        <button type="button" onClick={onClose} className="text-fg-subtle hover:text-fg" aria-label="Close">
          <X size={14} />
        </button>
      </div>

      {entryEditable && (
        <label className="flex items-center justify-between gap-3">
          <span className="text-fg-muted">Entry ({spec.entry.orderType})</span>
          <input className={input} value={entry} onChange={(e) => setEntry(e.target.value)} inputMode="decimal" />
        </label>
      )}

      {zone && !bought && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-fg-muted">Buy zone</span>
            <span className="flex items-center gap-1.5">
              <input className={input} value={zMin} onChange={(e) => setZMin(e.target.value)} inputMode="decimal" aria-label="Zone from" />
              <span className="text-fg-faint">–</span>
              <input className={input} value={zMax} onChange={(e) => setZMax(e.target.value)} inputMode="decimal" aria-label="Zone to" />
            </span>
          </div>
          <label className="flex items-center justify-between gap-3">
            <span className="text-fg-muted">Thesis level (cancel for good)</span>
            <input className={input} placeholder="none" value={zInvalid} onChange={(e) => setZInvalid(e.target.value)} inputMode="decimal" />
          </label>
          <label className="flex items-center justify-between gap-3">
            <span className="text-fg-muted">No-chase level (skip that day)</span>
            <input className={input} placeholder="none" value={zChase} onChange={(e) => setZChase(e.target.value)} inputMode="decimal" />
          </label>
          {zone.window?.kind === 'sessions' && (
            <label className="flex items-center justify-between gap-3">
              <span className="text-fg-muted">Sessions to watch (from the start)</span>
              <input className={input} value={zSessions} onChange={(e) => setZSessions(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" />
            </label>
          )}
          <div className="flex items-center justify-between gap-3">
            <span className="text-fg-muted">Under the zone</span>
            <select className={select} value={zBelow} onChange={(e) => setZBelow(e.target.value as BandSide)}>
              {SIDES.map((x) => (
                <option key={x.v} value={x.v}>
                  {x.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-fg-muted">Over the zone</span>
            <select className={select} value={zAbove} onChange={(e) => setZAbove(e.target.value as BandSide)}>
              {SIDES.map((x) => (
                <option key={x.v} value={x.v}>
                  {x.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        {spec.stopLosses.map((_, i) => (
          <label key={`sl${i}`} className="flex items-center justify-between gap-3">
            <span className="text-fg-muted">Stop {spec.stopLosses.length > 1 ? `SL${i + 1}` : ''}</span>
            <input className={input} value={stops[i]} onChange={(e) => setStops(stops.map((x, k) => (k === i ? e.target.value : x)))} inputMode="decimal" />
          </label>
        ))}
        {spec.takeProfits.map((_, i) => {
          const last = i === spec.takeProfits.length - 1
          return (
            <div key={`tp${i}`} className="flex items-center justify-between gap-3">
              <span className={cn('text-fg-muted', last && dropLast && 'line-through opacity-60')}>Target TP{i + 1}</span>
              <span className="flex items-center gap-2">
                {last && (
                  <label className="flex items-center gap-1 text-[11px] text-fg-subtle" title="The last target’s shares ride with their stop (a runner)">
                    <input type="checkbox" checked={dropLast} onChange={(e) => setDropLast(e.target.checked)} className="accent-[var(--color-accent)]" />
                    remove
                  </label>
                )}
                <input
                  className={input}
                  disabled={last && dropLast}
                  value={targets[i]}
                  onChange={(e) => setTargets(targets.map((x, k) => (k === i ? e.target.value : x)))}
                  inputMode="decimal"
                />
              </span>
            </div>
          )
        })}
        {runner && (
          <label className="flex items-center justify-between gap-3">
            <span className="text-fg-muted">Target for the runner</span>
            <input className={input} placeholder="none" value={runnerTarget} onChange={(e) => setRunnerTarget(e.target.value)} inputMode="decimal" />
          </label>
        )}
      </div>

      <div className="space-y-1.5 border-t border-[var(--separator)] pt-2.5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-fg-muted">Break-even step</span>
          <div className="inline-flex rounded-lg border border-[var(--line-soft)] p-0.5">
            <button type="button" onClick={() => setIns({ ...ins, on: false })} className={seg(!ins.on)}>
              Off
            </button>
            {[75, 85, 90, 95].map((lv) => (
              <button key={lv} type="button" onClick={() => setIns({ ...ins, on: true, level: lv })} className={seg(ins.on && ins.level === lv)}>
                {lv}%
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center justify-between gap-3">
          <span className="text-fg-muted">Protect others after a profit</span>
          <input type="checkbox" checked={ins.protectOthers} onChange={(e) => setIns({ ...ins, protectOthers: e.target.checked })} className="accent-[var(--color-accent)]" />
        </label>
        <div className="flex items-center justify-between gap-3">
          <span className="text-fg-muted">Stalls near the target → take the zone</span>
          <div className="inline-flex rounded-lg border border-[var(--line-soft)] p-0.5">
            <button type="button" onClick={() => setStall({ on: false })} className={seg(!stall.on)}>
              Off
            </button>
            {[0.5, 1, 2].map((k) => (
              <button key={k} type="button" onClick={() => setStall({ on: true, sigmas: k, way: null })} className={seg(stall.on && stall.way == null && stall.sigmas === k)}>
                {k}σ
              </button>
            ))}
          </div>
        </div>
        {stall.on && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-fg-muted">Wait: noise would have reached the target with</span>
            <div className="inline-flex rounded-lg border border-[var(--line-soft)] p-0.5">
              {([50, 70, 80, 90] as const).map((p) => (
                <button key={p} type="button" onClick={() => setStall({ confidence: p, minutes: null })} className={seg(stall.minutes == null && stall.confidence === p)}>
                  {p}%
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {spec.rules.length > 0 && (
        <div className="space-y-1 border-t border-[var(--separator)] pt-2.5">
          <div className="text-fg-muted">Rules — untick to remove</div>
          {w.summary.rulesText.map((r) => (
            <label key={r.id} className="flex items-start gap-2 text-[12px] text-fg-subtle">
              <input
                type="checkbox"
                checked={!dropRules.has(r.id)}
                onChange={(e) => {
                  const s = new Set(dropRules)
                  if (e.target.checked) s.delete(r.id)
                  else s.add(r.id)
                  setDropRules(s)
                }}
                className="mt-[3px] accent-[var(--color-accent)]"
              />
              <span className={cn(dropRules.has(r.id) && 'line-through opacity-60')}>{r.text}</span>
            </label>
          ))}
          <div className="text-[11px] text-fg-faint">New rules, a different split of the shares and anything else: ask in chat.</div>
        </div>
      )}

      {err && <div className="text-[12px] text-down">{err}</div>}
      <div className="flex items-center gap-2 border-t border-[var(--separator)] pt-2.5">
        <button type="button" disabled={busy} onClick={() => void submit()} className={cn(btn, 'bg-[var(--color-accent)] text-[var(--color-base)]')}>
          <Check size={13} /> {busy ? 'Preparing…' : 'Show the change'}
        </button>
        <span className="text-[11px] text-fg-faint">You approve it on the next card — nothing changes before.</span>
      </div>
    </div>
  )
}
